import { useEffect, useMemo, useState } from 'react'
import { useDisplayUserError } from '@yes.technology/react-toolkit'
import { useQuery } from '@tanstack/react-query'

import { AvailableCredentials, Credentials } from 'authentication/shared/types'
import { useAuthentication } from 'authentication/shared/hooks'
import useUpgradeToken from 'authentication/shared/hooks/useUpgradeToken'
import { fetchCredentials } from 'authentication/shared/api'
import tryJwtDecode from 'shared/utils/jwt'
import {
  CurrentSelections,
  getFilteredOptions,
  getAutoSelectedValue,
  fieldMappings,
  ResponseFilteredOptions
} from './credential-filter'

const useCredentials = () => {
  const { authenticatedData, startLoading, stopLoading } = useAuthentication()

  const upgradeToken = useUpgradeToken()

  const { displayUserError } = useDisplayUserError()

  const [credentialsLoaded, setCredentialsLoaded] = useState(false)

  const { token } = authenticatedData

  const { user_id: userUuid, credentials: tokenCredentials } =
    tryJwtDecode<{ user_id: string; credentials: Credentials }>(token || '') ||
    {}

  const {
    data: fetchedAvailableCredentials,
    isFetching: isFetchingCredentials,
    isFetched,
    error
  } = useQuery<AvailableCredentials | null, Error>({
    queryKey: ['filteraction', userUuid],
    queryFn: () => fetchCredentials(userUuid as string),
    enabled: !authenticatedData.available_credentials && !!userUuid
  })

  useEffect(() => {
    if (isFetched && error) {
      displayUserError({ error })
      console.log(error)
    }
  }, [error, displayUserError, isFetched])

  const [selectedCredentials, setSelectedCredentials] = useState<Credentials>({
    applicationsystem_uuid: tokenCredentials?.applicationsystem_uuid || '',
    organization_uuid: tokenCredentials?.organization_uuid || '',
    user_uuid: tokenCredentials?.user_uuid || ''
  })

  const credentials = useMemo(() => {
    const cachedAvailableCredentials = authenticatedData?.available_credentials
    const availableCredentials =
      cachedAvailableCredentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const filteredOptions = getFilteredOptions(
      availableCredentials,
      selectedCredentials
    )

    return {
      applicationSystem: {
        options: filteredOptions.applicationsystems_info
      },
      organization: {
        options: filteredOptions.organizations_info
      },
      user: {
        options: filteredOptions.user_info
      }
    }
  }, [authenticatedData, fetchedAvailableCredentials, selectedCredentials])

  const submitCredentials = async (
    onSuccess: (upgradedToken: string) => void,
    onError: (error?: unknown) => void
  ) => {
    startLoading()

    try {
      const upgradedToken = await upgradeToken(selectedCredentials)
      upgradedToken && onSuccess?.(upgradedToken)
    } catch (error) {
      onError?.(error)
      displayUserError({ error })
      console.error(error)
    } finally {
      stopLoading()
    }
  }

  const setEcosystem = (ecosystemUuid: string) => {
    setSelectedCredentials((prev) => ({
      ...prev,
      ecosystem_uuid: ecosystemUuid
    }))
  }

  const setField = (uuid: string, field: keyof CurrentSelections) => {
    const cachedAvailableCredentials = authenticatedData?.available_credentials
    const availableCredentials =
      cachedAvailableCredentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const currentSelections: CurrentSelections = {
      ...selectedCredentials
    }
    currentSelections[field] = uuid

    const filteredOptions = getFilteredOptions(
      availableCredentials,
      currentSelections
    )

    const updatedCredentials: Credentials = {
      applicationsystem_uuid: selectedCredentials.applicationsystem_uuid,
      organization_uuid: selectedCredentials.organization_uuid,
      user_uuid: selectedCredentials.user_uuid
    }

    updatedCredentials[field] = uuid

    const mapping = fieldMappings[field]
    if (mapping) {
      mapping.dependentFields.forEach(({ field: dependentField, options }) => {
        const optionsKey = options as keyof ResponseFilteredOptions
        const currentValue =
          updatedCredentials[dependentField as keyof Credentials]
        updatedCredentials[dependentField as keyof Credentials] =
          getAutoSelectedValue(filteredOptions[optionsKey], currentValue, uuid)
      })
    }

    setSelectedCredentials(updatedCredentials)
  }

  const isCredentialsValid = useMemo(() => {
    return (
      selectedCredentials.applicationsystem_uuid &&
      selectedCredentials.organization_uuid &&
      selectedCredentials.user_uuid
    )
  }, [selectedCredentials])

  // select credentials if there is only one option
  useEffect(() => {
    if (credentialsLoaded || !credentials) return

    setCredentialsLoaded(true)

    const appOptions = credentials.applicationSystem.options
    const orgOptions = credentials.organization.options
    const userOptions = credentials.user.options
    const applicationUuid = appOptions.length === 1 ? appOptions[0].id : ''
    const organizationUuid = orgOptions.length === 1 ? orgOptions[0].id : ''
    const userUuid = userOptions.length === 1 ? userOptions[0].id : ''

    setSelectedCredentials({
      applicationsystem_uuid: applicationUuid,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    })
  }, [credentials, credentialsLoaded])

  return {
    credentials,
    selectedCredentials,
    isCredentialsValid,
    isLoading: isFetchingCredentials,
    submitCredentials,
    setEcosystem,
    setField
  }
}

export default useCredentials
